import { makeAutoObservable } from 'mobx'
import { AxiosError } from 'axios'
import { uiStore } from 'shared/store/uiStore'
import { logger } from 'shared/lib'
import { toastStore } from 'shared/ui'
import { ComplianceApi } from 'entities/Compliance'
import { Phone } from 'entities/Phone/model/Phone'
import { type IResponseNumber } from 'entities/Phone/api/types'
import { PhoneApi, AssignNumberCommonStore } from 'entities/Phone'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type NumbersLayerStore } from 'pages/settings/pages/numbers/store/numbersLayerStore'
import { ChooseNumberStore } from 'pages/settings/pages/numbers/pages/BuyNumber/ui/ChooseNumber/store/ChooseNumberStore'
import { NumbersPath } from 'pages/settings/pages/numbers/route/numbersPath'
import { PaymentCommonStore } from 'pages/settings/ui/PaymentCommon/store/paymentCommonStore'
import { SettingsPath } from 'pages/settings/route/settingsPath'

export enum BuyNumberRoutesSteps {
  ChooseNumber = 'choose-number',
  AssignNumber = 'assign-number',
  Payment = 'payment',
}

const titleMap: Record<string, string> = {
  [BuyNumberRoutesSteps.ChooseNumber]: 'Choose number',
  [BuyNumberRoutesSteps.AssignNumber]: 'Assign number',
  [BuyNumberRoutesSteps.Payment]: 'Payment',
}

export class BuyNumberStepsStore {
  private _currentStep = 1
  private readonly _chooseNumberStore: ChooseNumberStore
  private readonly _assignNumberStore: AssignNumberCommonStore
  private readonly _paymentStore: PaymentCommonStore

  constructor(private _numbersLayerStore: NumbersLayerStore, private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this._chooseNumberStore = new ChooseNumberStore(this._entities)
    this._assignNumberStore = new AssignNumberCommonStore(
      {
        nextClickHandler: this.nextClickHandler,
      },
      this._entities
    )
    this._paymentStore = new PaymentCommonStore(
      this.chooseNumberStore.chooseNumberTabsStore.refresh,
      this._entities
    )

    this.init()
  }

  init = () => {
    this._entities.complianceStore.loadNumberVendors()
  }

  get numbersLayerStore() {
    return this._numbersLayerStore
  }

  get assignNumberStore() {
    return this._assignNumberStore
  }

  get chooseNumberStore() {
    return this._chooseNumberStore
  }

  get paymentStore() {
    return this._paymentStore
  }

  get dropdownCardsStore() {
    return this._paymentStore.dropdownCardsStore
  }

  get rootPath() {
    return NumbersPath.buyNumber.steps
  }

  get isBuyNumberAction() {
    return (
      this._entities.organizationStore.isTwilioNumberVendor &&
      this._assignNumberStore.assignType === 'later'
    )
  }

  get stepMap() {
    if (this.isBuyNumberAction) {
      return {
        1: BuyNumberRoutesSteps.ChooseNumber,
        2: BuyNumberRoutesSteps.AssignNumber,
      }
    }
    return {
      1: BuyNumberRoutesSteps.ChooseNumber,
      2: BuyNumberRoutesSteps.AssignNumber,
      3: BuyNumberRoutesSteps.Payment,
    }
  }

  get getCurrentStep() {
    return this.getStep(0)
  }

  get getNextStep() {
    const step = this.getStep(1)
    if (!step) return this.getCurrentStep
    return step
  }

  get getLastStep() {
    const step = this.getStep(-1)
    if (!step) return this.getCurrentStep
    return step
  }

  get getCurrentUrl() {
    return `${this.rootPath}/${this.getCurrentStep}`
  }

  get getNextUrl() {
    return `${this.rootPath}/${this.getNextStep}`
  }

  get getLastUrl() {
    return `${this.rootPath}/${this.getLastStep}`
  }

  get allStepsLength() {
    return Object.keys(this.stepMap).length
  }

  get assignedInboxName() {
    if (this.assignNumberStore.assignType === 'existing') {
      const { onlyInboxesList } = this._entities.inboxesStore

      return onlyInboxesList.find((inbox) => inbox.id === this.assignNumberStore.activeInboxId)
        ?.name
    }

    if (this.assignNumberStore.assignType === 'new') {
      return this.assignNumberStore.newInboxStore.newInboxName
    }

    return null
  }

  setCurrentStep = (step: number) => {
    this._currentStep = step
  }

  getStep = (direction: -1 | 1 | 0) => {
    const allSteps = Object.values(this.stepMap)
    const currentPageIndex = this._currentStep - 1

    return allSteps[currentPageIndex + direction]
  }

  getStepByPath = (buyNumberRoutes: BuyNumberRoutesSteps) => {
    const allPaths = Object.entries(this.stepMap)
    const stepIndex = allPaths.findIndex((path) => {
      return path[1] === buyNumberRoutes
    })

    return stepIndex !== -1 ? stepIndex + 1 : 1
  }

  getStepUrlMap = (buyNumberRoutes: BuyNumberRoutesSteps) => {
    return `${this.rootPath}/${buyNumberRoutes}`
  }

  decreaseStep = () => {
    uiStore.changeRoute({
      path: this.getLastUrl,
    })
  }

  nextClickHandler = () => {
    uiStore.changeRoute({
      path: this.getNextUrl,
    })
  }

  getSettingsStep = (path: BuyNumberRoutesSteps) => {
    return this.getStepByPath(path)
  }

  getProgressInfoTitle = (pathname: string) => {
    return titleMap[pathname] || ''
  }

  afterAddNumberHandler = async (phone: IResponseNumber) => {
    toastStore.add({
      type: 'success',
      title: 'Number purchased',
    })

    const addUseCaseLater = this.chooseNumberStore.selectedUseCaseId === -1
    const noUseCasesYet = this.chooseNumberStore.useCases.length === 0
    const noCompletedUseCases = this.chooseNumberStore.completedUseCases.length === 0

    const needVerify =
      phone.is_toll_free && (addUseCaseLater || noUseCasesYet || noCompletedUseCases)
    if (needVerify) {
      this._entities.complianceStore.setUnverifiedTollFreeNumber(new Phone(phone, this._entities))
    }

    const { selectedUseCaseId, useCaseNumberIds } = this.chooseNumberStore

    if (selectedUseCaseId !== -1 && selectedUseCaseId) {
      await ComplianceApi.submitTollFreeForReview(selectedUseCaseId, {
        numbers: useCaseNumberIds ? [...useCaseNumberIds, phone.id] : [phone.id],
        additional_numbers: [phone.id],
      })
    }

    uiStore.changeRoute({
      path: `/${SettingsPath.numbers.root}`,
    })

    if (needVerify && !this._entities.organizationStore.isTwilioNumberVendor) {
      setTimeout(() => {
        this.numbersLayerStore.settingsStore.openVerifyNumberModal(
          phone,
          this.assignedInboxName || ''
        )
      }, 500)
    }
  }

  confirmNumber = async () => {
    const { newInboxStore } = this.assignNumberStore
    const { selectedNumber, isUKSelected } = this.chooseNumberStore.chooseNumberTabsStore
    if (!selectedNumber) return

    this.paymentStore.clearError()

    try {
      this.numbersLayerStore.stepsStore.setStepLoading(true)
      const number_vendor_id = this._entities.complianceStore.numberVendorId

      if (
        this.assignNumberStore.assignType === 'existing' &&
        this.assignNumberStore.activeInboxId
      ) {
        const { data } = await ComplianceApi.addTollFreeNumberToExistingInbox(
          this.assignNumberStore.activeInboxId,
          {
            members: null,
            name: '',
            number: selectedNumber.number,
            number_vendor_id,
            owner: {
              id: null,
            },
            vendor_id: '',
            is_mobile: isUKSelected || null,
            payment_method: this.dropdownCardsStore.activeId,
          }
        )

        if (data) {
          const targetNumber = data.find((item) => item.number === selectedNumber?.number)

          targetNumber && this.afterAddNumberHandler(targetNumber)
        }
      }

      if (this.assignNumberStore.assignType === 'new') {
        const { data } = await ComplianceApi.addTollFreeNumberWithNewInbox({
          members: newInboxStore.selectedMembers.map((item) => String(item.id)),
          name: newInboxStore.newInboxNameTrimmed,
          number: selectedNumber.number,
          number_vendor_id,
          owner: {
            id:
              this._entities.usersStore.users.length === 1
                ? this._entities.usersStore.users[0].id
                : Number(newInboxStore.activeInboxOwner),
          },
          vendor_id: '',
          is_mobile: isUKSelected || null,
          payment_method: this.dropdownCardsStore.activeId,
        })

        if (data) {
          this.afterAddNumberHandler(data.number)
        }
      }

      if (this.assignNumberStore.assignType === 'later') {
        const { data } = await PhoneApi.createNumbersUnassigned({
          busy: true,
          successful: false,
          original: {
            number: '',
            number_vendor_id: '',
            vendor_id: '',
            name: '',
            members: null,
            owner: {
              id: null,
            },
          },
          persistChanges: false,
          useEmptyField: true,
          number: selectedNumber.number,
          number_vendor_id,
          vendor_id: '',
          name: '',
          members: null,
          owner: {
            id: null,
          },
          errors: {
            errors: {},
          },
          is_mobile: isUKSelected || null,
          payment_method: this.dropdownCardsStore.activeId,
        })

        if (data) {
          this.afterAddNumberHandler(data)
        }
      }
    } catch (e) {
      if (e instanceof AxiosError) {
        if (e.errors.getError('stripe') === 'Your card was declined.') {
          this.paymentStore.setPaymentError(true)
        }

        if (e.errors.getError('number') === 'Phone number is no longer available.') {
          this.paymentStore.setNoAvailableError(true)
        }
      }

      logger.error(e)
    } finally {
      this.numbersLayerStore.stepsStore.setStepLoading(false)
    }
  }
}
